"use client";

import { motion } from "motion/react";
import { useState } from "react";

import { readForm, submitAccessRequest } from "@/lib/access-request";
import {
  Field,
  FormError,
  Honeypot,
  PreferEmail,
  SubmitButton,
  SuccessState,
  inputClass,
  selectClass,
} from "@/components/FormFields";

const GOALS = [
  "Scale conversions",
  "Test creator ads",
  "Improve CAC",
  "Brand lift",
  "Other",
];

/**
 * The Book a Demo form on the Brand tab of /request-access.
 * Submissions are stored as `brand` access requests.
 */
export function DemoRequestForm() {
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [goals, setGoals] = useState<string[]>([]);

  function toggleGoal(g: string) {
    setGoals((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await submitAccessRequest("brand", { ...readForm(e.currentTarget), goals });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setPending(false);
    }
  }

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="h-full"
      >
        <SuccessState title="Thank you! We'll reach out soon." />
      </motion.div>
    );
  }

  return (
    <>
      <h2 className="text-3xl font-display font-semibold text-gray-900 mb-8">
        Request A Demo
      </h2>

      <form onSubmit={handleSubmit} className="relative space-y-5">
        <Honeypot />

        {/* Full Name + Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name" htmlFor="demo-fullName" required>
            <input
              id="demo-fullName"
              name="fullName"
              required
              type="text"
              autoComplete="name"
              placeholder="First Name"
              className={inputClass}
            />
          </Field>
          <Field label="Email" htmlFor="demo-email" required>
            <input
              id="demo-email"
              name="email"
              required
              type="email"
              autoComplete="email"
              placeholder="Email ID"
              className={inputClass}
            />
          </Field>
        </div>

        {/* Phone + Company */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone Number" htmlFor="demo-phone" required>
            <div className="flex">
              <span className="inline-flex items-center gap-1.5 px-3 rounded-l-xl border border-r-0 border-gray-200 bg-gray-100 text-sm text-gray-500 shrink-0">
                🇺🇸 +1
              </span>
              <input
                id="demo-phone"
                name="phone"
                type="tel"
                autoComplete="tel-national"
                placeholder="Mobile Number"
                className="w-full rounded-r-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-base sm:text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition"
              />
            </div>
          </Field>
          <Field label="Company" htmlFor="demo-company" required>
            <input
              id="demo-company"
              name="company"
              required
              type="text"
              autoComplete="organization"
              placeholder="Enter company name"
              className={inputClass}
            />
          </Field>
        </div>

        {/* Role + Monthly spend */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Role" htmlFor="demo-role">
            <select id="demo-role" name="role" className={selectClass}>
              <option value="">Select an option</option>
              <option>Brand / Marketing Team</option>
              <option>Agency</option>
              <option>Creator / Influencer</option>
              <option>Other</option>
            </select>
          </Field>
          <Field label="Monthly paid social spend" htmlFor="demo-spend" required>
            <select id="demo-spend" name="monthlySpend" required className={selectClass}>
              <option value="">Select an Option</option>
              <option>Under $10k</option>
              <option>$10k – $50k</option>
              <option>$50k – $200k</option>
              <option>$200k+</option>
            </select>
          </Field>
        </div>

        {/* Primary goal */}
        <div>
          <p className="block text-sm font-semibold text-gray-900 mb-3">
            Primary goal
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-0 sm:gap-y-2.5 gap-x-2">
            {GOALS.map((g) => (
              <label key={g} className="flex items-center gap-2 cursor-pointer group min-h-11 sm:min-h-0">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={goals.includes(g)}
                  onChange={() => toggleGoal(g)}
                />
                <div
                  className={`w-4 h-4 rounded border-2 shrink-0 transition-all flex items-center justify-center peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-1 ${
                    goals.includes(g)
                      ? "border-brand-600 bg-brand-600"
                      : "border-gray-300 bg-white group-hover:border-brand-400"
                  }`}
                >
                  {goals.includes(g) && (
                    <svg
                      className="w-2.5 h-2.5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-sm text-gray-700">{g}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Notes */}
        <Field label="Notes" htmlFor="demo-notes">
          <textarea
            id="demo-notes"
            name="notes"
            rows={3}
            placeholder="Anything you'd like us to know before the call..."
            className={`${inputClass} resize-none`}
          />
        </Field>

        <p className="text-xs text-gray-400">
          By submitting, you agree to our{" "}
          <a href="/terms-of-service" className="text-brand-600 hover:underline cursor-pointer">
            Terms
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" className="text-brand-600 hover:underline cursor-pointer">
            Privacy Policy
          </a>
          .
        </p>

        <FormError message={error} />

        <SubmitButton pending={pending}>Request a Demo</SubmitButton>

        <PreferEmail />
      </form>
    </>
  );
}
